import mustache from "mustache";
import {
  calcularMonitoreoBroca,
  MonitoreoBrocaConteos,
  MonitoreoBrocaRequest,
} from "../../../../../modulos/monitoreo/broca/request/MonitoreoBrocaRequest";
import { getIsoWeek, toISODate } from "../../../../../utils/fechaUtils";
import { _2d } from "../../../../../utils/numberUtils";
import { IFormBodyRender } from "../../../../definitions/form/IFormBodyRender";
import { IFormField } from "../../../../definitions/form/IFormField";
import { InputDateField } from "../../../../implementations/form/date/InputDateField";
import { InputNumberField } from "../../../../implementations/form/number/InputNumberField";
import { InputReadonlyField } from "../../../../implementations/form/readonly/InputReadonlyField";
import { LoteSelectorField } from "../../../terceros/lote/form/LoteSelectorField";
import template from "./monitoreoBrocaFormTemplate.html";

mustache.parse(template);

/** Formulario de monitoreo de broca (Render/Connect). Expone `validate()` para
 *  que el modal contenedor decida si guardar. En reconstrucción: secciones
 *  quitadas, se agregan de nuevo una por una. */
export class MonitoreoBrocaFormRender implements IFormBodyRender {
  private readonly _request: MonitoreoBrocaRequest;

  // === Card "Datos básicos" ===
  private readonly _lote: LoteSelectorField<MonitoreoBrocaRequest>;
  private readonly _fecha: InputDateField<MonitoreoBrocaRequest>;
  private readonly _semana: InputReadonlyField;

  // === Card "Datos de campo" ===
  private readonly _arbolesEvaluados: InputNumberField<MonitoreoBrocaRequest>;
  private readonly _arbolesBrocados: InputNumberField<MonitoreoBrocaRequest>;
  private readonly _frutosEvaluados: InputNumberField<MonitoreoBrocaRequest>;
  private readonly _frutosBrocados: InputNumberField<MonitoreoBrocaRequest>;
  private readonly _posicion1: InputNumberField<MonitoreoBrocaRequest>;
  private readonly _posicion2: InputNumberField<MonitoreoBrocaRequest>;
  private readonly _ausentes: InputNumberField<MonitoreoBrocaRequest>;
  private readonly _muertas: InputNumberField<MonitoreoBrocaRequest>;

  private readonly _arbolesLimpios: InputReadonlyField;
  private readonly _totales: InputReadonlyField;
  private readonly _porcentajePosicion1: InputReadonlyField;
  private readonly _porcentajePosicion2: InputReadonlyField;
  private readonly _porcentajeAusentes: InputReadonlyField;
  private readonly _porcentajeMuertas: InputReadonlyField;

  // === Card "Resultados" ===
  private readonly _promedioGranosXRama: InputReadonlyField;
  private readonly _infestacionGeneral: InputReadonlyField;
  private readonly _infestacionReal: InputReadonlyField;
  private readonly _mortalidad: InputReadonlyField;

  /** Campos con validación (para `validate`). */
  private readonly _fields: IFormField[];
  /** Campos de solo lectura (connect/dispose). */
  private readonly _readonlyFields: InputReadonlyField[];

  constructor(request: MonitoreoBrocaRequest) {
    this._request = request;
    this._lote = this._createLote();
    this._fecha = this._createFecha();
    this._semana = new InputReadonlyField({
      label: "Semana",
      description: "Semana del año",
      value: String(getIsoWeek(this._request.fecha)),
    });

    this._arbolesEvaluados = this._createConteo(
      "arbolesEvaluados",
      "Árboles evaluados",
      true,
    );
    this._arbolesBrocados = this._createConteo(
      "arbolesBrocados",
      "Árboles brocados",
      false,
    );
    this._frutosEvaluados = this._createConteo(
      "frutosEvaluados",
      "Frutos evaluados",
      true,
    );
    this._frutosBrocados = this._createConteo(
      "frutosBrocados",
      "Frutos brocados",
      false,
    );
    this._posicion1 = this._createConteo("posicion1", "Posición 1", false);
    this._posicion2 = this._createConteo("posicion2", "Posición 2", false);
    this._ausentes = this._createConteo("ausentes", "Ausentes", false);
    this._muertas = this._createConteo("muertas", "Muertas", false);

    this._arbolesLimpios = this._createReadonly(
      "Árboles limpios",
      "evaluados − brocados",
    );
    this._totales = this._createReadonly(
      "Totales",
      "pos. 1 + pos. 2 + ausentes + muertas",
    );
    this._porcentajePosicion1 = this._createReadonly(
      "% Posición 1",
      "sobre el total",
    );
    this._porcentajePosicion2 = this._createReadonly(
      "% Posición 2",
      "sobre el total",
    );
    this._porcentajeAusentes = this._createReadonly(
      "% Ausentes",
      "sobre el total",
    );
    this._porcentajeMuertas = this._createReadonly(
      "% Muertas",
      "sobre el total",
    );

    this._promedioGranosXRama = this._createReadonly(
      "Promedio granos x rama",
      "frutos evaluados / árboles evaluados",
    );
    this._infestacionGeneral = this._createReadonly(
      "Infestación general",
      "frutos brocados / frutos evaluados",
    );
    this._infestacionReal = this._createReadonly(
      "Infestación real",
      "descuenta broca muerta/ausente",
    );
    this._mortalidad = this._createReadonly("Mortalidad", "% de broca muerta");

    this._fields = [
      this._lote,
      this._fecha,
      this._arbolesEvaluados,
      this._arbolesBrocados,
      this._frutosEvaluados,
      this._frutosBrocados,
      this._posicion1,
      this._posicion2,
      this._ausentes,
      this._muertas,
    ];
    this._readonlyFields = [
      this._semana,
      this._arbolesLimpios,
      this._totales,
      this._porcentajePosicion1,
      this._porcentajePosicion2,
      this._porcentajeAusentes,
      this._porcentajeMuertas,
      this._promedioGranosXRama,
      this._infestacionGeneral,
      this._infestacionReal,
      this._mortalidad,
    ];
  }

  render(): string {
    return mustache.render(template, {
      lote: this._lote.render(),
      fecha: this._fecha.render(),
      semana: this._semana.render(),
      arbolesEvaluados: this._arbolesEvaluados.render(),
      arbolesBrocados: this._arbolesBrocados.render(),
      frutosEvaluados: this._frutosEvaluados.render(),
      frutosBrocados: this._frutosBrocados.render(),
      posicion1: this._posicion1.render(),
      posicion2: this._posicion2.render(),
      ausentes: this._ausentes.render(),
      muertas: this._muertas.render(),
      arbolesLimpios: this._arbolesLimpios.render(),
      totales: this._totales.render(),
      porcentajePosicion1: this._porcentajePosicion1.render(),
      porcentajePosicion2: this._porcentajePosicion2.render(),
      porcentajeAusentes: this._porcentajeAusentes.render(),
      porcentajeMuertas: this._porcentajeMuertas.render(),
      promedioGranosXRama: this._promedioGranosXRama.render(),
      infestacionGeneral: this._infestacionGeneral.render(),
      infestacionReal: this._infestacionReal.render(),
      mortalidad: this._mortalidad.render(),
    });
  }

  connect(): void {
    this._fields.forEach((field) => field.connect());
    this._readonlyFields.forEach((field) => field.connect());

    this._fecha.setOnChange(() => this._refreshSemana());

    const onChangeConteo = () => this._refreshCalculados();
    this._arbolesEvaluados.setOnChange(onChangeConteo);
    this._arbolesBrocados.setOnChange(onChangeConteo);
    this._frutosEvaluados.setOnChange(onChangeConteo);
    this._frutosBrocados.setOnChange(onChangeConteo);
    this._posicion1.setOnChange(onChangeConteo);
    this._posicion2.setOnChange(onChangeConteo);
    this._ausentes.setOnChange(onChangeConteo);
    this._muertas.setOnChange(onChangeConteo);

    this._refreshCalculados();
  }

  dispose(): void {
    this._fields.forEach((field) => field.dispose());
    this._readonlyFields.forEach((field) => field.dispose());
  }

  /** Valida todos los campos; retorna `true` si son válidos. */
  validate(): boolean {
    let isValid = true;
    this._fields.forEach((field) => {
      field.showError();
      if (field.getError()) {
        if (isValid) field.focus();
        isValid = false;
      }
    });
    return isValid;
  }

  /** Selector de lote, filtrado a lotes con monitoreo de café habilitado. */
  private _createLote(): LoteSelectorField<MonitoreoBrocaRequest> {
    return new LoteSelectorField(
      this._request,
      {
        title: "Lote",
        get: (r) => r.loteId,
        set: (r, v) => {
          r.loteId = v;
        },
        filter: (lote) => lote.cultivo?.isMonitoreoCafe ?? false,
      },
      { isRequired: true },
    );
  }

  /** Fecha del monitoreo (máximo hoy). */
  private _createFecha(): InputDateField<MonitoreoBrocaRequest> {
    return new InputDateField(this._request, {
      label: "Fecha",
      description: "Fecha que se realiza el monitoreo",
      max: toISODate(new Date()),
      get: (r) => r.fecha,
      set: (r, v) => {
        r.fecha = v;
      },
    });
  }

  /** Campo numérico de un conteo de campo (entero, mínimo 1 si es requerido). */
  private _createConteo(
    key: keyof MonitoreoBrocaConteos,
    label: string,
    required: boolean,
  ): InputNumberField<MonitoreoBrocaRequest> {
    return new InputNumberField(
      this._request,
      {
        label,
        get: (r) => r[key],
        set: (r, v) => {
          r[key] = v;
        },
      },
      { isRequired: required, min: required ? 1 : 0 },
    );
  }

  private _createReadonly(
    label: string,
    description: string,
  ): InputReadonlyField {
    return new InputReadonlyField({ label, description });
  }

  /** Recalcula la semana ISO mostrada a partir de la fecha actual. */
  private _refreshSemana(): void {
    this._semana.setValue(String(getIsoWeek(this._request.fecha)));
  }

  /** Recalcula los índices derivados y los escribe en el request. */
  private _refreshCalculados(): void {
    const conteos: MonitoreoBrocaConteos = {
      arbolesEvaluados: this._request.arbolesEvaluados,
      arbolesBrocados: this._request.arbolesBrocados,
      frutosEvaluados: this._request.frutosEvaluados,
      frutosBrocados: this._request.frutosBrocados,
      posicion1: this._request.posicion1,
      posicion2: this._request.posicion2,
      ausentes: this._request.ausentes,
      muertas: this._request.muertas,
    };
    const calculados = calcularMonitoreoBroca(conteos);
    Object.assign(this._request, calculados);

    this._arbolesLimpios.setValue(String(calculados.arbolesLimpios));
    this._totales.setValue(String(calculados.totales));
    this._porcentajePosicion1.setValue(
      `${_2d(calculados.porcentajePosicion1)}%`,
    );
    this._porcentajePosicion2.setValue(
      `${_2d(calculados.porcentajePosicion2)}%`,
    );
    this._porcentajeAusentes.setValue(`${_2d(calculados.porcentajeAusentes)}%`);
    this._porcentajeMuertas.setValue(`${_2d(calculados.porcentajeMuertas)}%`);
    this._promedioGranosXRama.setValue(_2d(calculados.promedioGranosXRama));
    this._infestacionGeneral.setValue(`${_2d(calculados.infestacionGeneral)}%`);
    this._infestacionReal.setValue(`${_2d(calculados.infestacionReal)}%`);
    this._mortalidad.setValue(`${_2d(calculados.mortalidad)}%`);
  }
}
